/**
 * Where a lead came from, for the browser: the keys a lead form keeps (the same list
 * as utils/crmAttribution.js on the server), their labels, the short "Came from"
 * text, and the website embed snippet that carries them into the form. No imports
 * and only erasable types, so node --test loads this file directly
 * (test/crm-attribution.test.js).
 */

/** UTM tags and ad click ids, in the order the contact drawer and the export list them. */
export const ATTR_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id",
  "gclid", "gbraid", "wbraid", "gad_source", "gad_campaignid",
  "fbclid", "fbc", "fbp", "msclkid", "ttclid", "li_fat_id", "ref",
] as const;

export type Attribution = Partial<Record<(typeof ATTR_KEYS)[number] | "landing" | "referrer", string>>;
export type VisitJourney = Attribution & { first_touch?: Attribution; last_touch?: Attribution; tracking_consent?: boolean };
export const JOURNEY_KEY = "nla_form_touches";
export type AttrRow = { key: string; label: string; value: string; kind: "text" | "id" | "url" };

const LABELS: Record<string, string> = {
  utm_source: "Source",
  utm_medium: "Medium",
  utm_campaign: "Campaign",
  utm_term: "Term",
  utm_content: "Content",
  utm_id: "Campaign ID",
  gclid: "Google click ID",
  gbraid: "GBRAID",
  wbraid: "WBRAID",
  gad_source: "Google Ads source",
  gad_campaignid: "Google Ads campaign ID",
  fbclid: "Meta click ID",
  fbc: "Meta click cookie",
  fbp: "Meta browser cookie",
  msclkid: "Microsoft click ID",
  ttclid: "TikTok click ID",
  li_fat_id: "LinkedIn click ID",
  ref: "Ref",
  landing: "Landing page",
  referrer: "Referrer",
};
/** Long opaque ids: shown cut short, with the full value on hover and a copy button. */
const ID_KEYS = new Set(["gclid", "gbraid", "wbraid", "fbclid", "fbc", "fbp", "msclkid", "ttclid", "li_fat_id"]);

/** Meta's documented cookie format. Never invent a browser id or refresh a click's timestamp. */
export function validMetaId(key: "fbc" | "fbp", value: unknown, now = Date.now()): string {
  if (typeof value !== "string" || value.length > 500) return "";
  const m = /^fb\.[0-9]\.(\d{13})\.([A-Za-z0-9_-]+)$/.exec(value);
  return m && Number(m[1]) >= 946684800000 && Number(m[1]) <= now + 300000 && (key !== "fbp" || /^\d+$/.test(m[2])) ? value : "";
}

export function browserCookies(): string { try { return document.cookie; } catch { return ""; } }

export function metaIdentifiers(visit: Attribution, { cookie = "", at, now = Date.now() }: { cookie?: string; at?: number; now?: number } = {}): Attribution {
  const read = (key: "fbc" | "fbp") => {
    const part = cookie.split(";").find(v => v.trim().startsWith(`_${key}=`));
    try { return validMetaId(key, part ? decodeURIComponent(part.trim().slice(5)) : "", now); } catch { return ""; }
  };
  const out: Attribution = { ...visit };
  const candidates = [validMetaId("fbc", visit.fbc, now), read("fbc")];
  let fbc = candidates.find(v => v && (!visit.fbclid || v.split(".").slice(3).join(".") === visit.fbclid)) || "";
  if (!fbc && visit.fbclid && /^[A-Za-z0-9_-]{1,200}$/.test(visit.fbclid) && at != null)
    fbc = validMetaId("fbc", `fb.1.${Math.trunc(at)}.${visit.fbclid}`, now);
  const fbp = validMetaId("fbp", visit.fbp, now) || read("fbp");
  delete out.fbc; delete out.fbp;
  if (fbc) out.fbc = fbc;
  if (fbp) out.fbp = fbp;
  return out;
}

/** An http(s) address, or "" (never a javascript: link). */
export const webUrl = (v: unknown): string => (typeof v === "string" && /^https?:\/\/[^\s]+$/i.test(v.trim()) ? v.trim() : "");

/** "https://www.google.com/search?q=x" → "google.com". */
export function hostOf(url: unknown): string {
  const m = /^https?:\/\/([^/?#:]+)/i.exec(webUrl(url));
  return m ? m[1].toLowerCase().replace(/^www\./, "") : "";
}

/** "https://site.com/casinos?x=1" → "site.com/casinos": an address the way people read it. */
export const shortUrl = (url: string) => url.replace(/^https?:\/\/(www\.)?/i, "").replace(/[?#].*$/, "").replace(/\/$/, "");

/** The attribution keys in a query string, each cut to 200 characters. */
export function pickAttribution(search: string): Attribution {
  const params = new URLSearchParams(search);
  const keep: Attribution = {};
  for (const key of ATTR_KEYS) {
    const v = params.get(key);
    if (v) { const clean = key === "fbc" || key === "fbp" ? validMetaId(key, v) : v.slice(0, 200); if (clean) keep[key] = clean; }
  }
  return keep;
}

/** Google Ads counts a conversion up to 90 days after the click, so a visit is remembered that long. */
export const VISIT_MS = 90 * 24 * 3600 * 1000;
/** Where a visit is saved: the same key the website embed snippet uses, so each reads the other's. */
export const VISIT_KEY = "nla_form_attr";

type VisitStore = { getItem(key: string): string | null; setItem(key: string, value: string): void };

/**
 * Where this visitor came from, on a page of the site itself (a NOLA landing page, or a
 * form or booking page opened on its own). With UTM tags or click ids in the address it
 * saves them, the page and the referring site for 90 days; without, it uses the newest
 * saved visit (its own, or the one the NOLA tracking script t.js keeps in nla_attr). So an
 * ad click still reaches the lead when the visitor reads another page first or comes back
 * days later. The embed snippet's rules, in TypeScript. Nothing is saved under Global
 * Privacy Control, and blocked storage only means nothing is remembered.
 */
export function rememberedVisit({ search, here, referrer = "", storage, now = Date.now(), gpc = false, cookie = "" }: {
  search: string;
  /** This page's address without its query string. */
  here: string;
  referrer?: string;
  storage?: VisitStore | null;
  now?: number;
  gpc?: boolean;
  cookie?: string;
}): Attribution {
  if (gpc) return {};
  const site = hostOf(here);
  const from = hostOf(referrer) && hostOf(referrer) !== site ? referrer.slice(0, 500) : "";
  const keys = pickAttribution(search);
  if (Object.keys(keys).length) {
    let old: Attribution = {};
    try { const raw = JSON.parse(storage?.getItem(VISIT_KEY) || "{}"); if (raw && typeof raw === 'object') old = raw; } catch { /* unavailable */ }
    const priorFbc = !keys.fbc && keys.fbclid && old.fbclid === keys.fbclid ? validMetaId("fbc", old.fbc, now) : "";
    const visit: Attribution = metaIdentifiers({ ...keys, ...(priorFbc ? { fbc: priorFbc } : {}), ...(webUrl(here) ? { landing: here.slice(0, 500) } : {}), ...(from ? { referrer: from } : {}) }, { cookie, at: now, now });
    try { if (!gpc) storage?.setItem(VISIT_KEY, JSON.stringify({ ...visit, at: now })); } catch { /* blocked storage */ }
    return visit;
  }
  const read = (key: string): Record<string, unknown> | null => {
    try { const v = JSON.parse(storage?.getItem(key) || "null"); return v && typeof v === "object" ? v : null; } catch { return null; }
  };
  const fresh = (v: Record<string, unknown> | null) => (v && typeof v.at === "number" && v.at <= now && now - v.at < VISIT_MS ? v : null);
  const own = fresh(read(VISIT_KEY));
  const t = read("nla_attr");
  const script = fresh((t?.last || t?.first || null) as Record<string, unknown> | null);
  const newest = script && (!own || (script.at as number) > (own.at as number)) ? script : own;
  if (!newest) return metaIdentifiers(from ? { referrer: from } : {}, { cookie, now });
  const out: Attribution = {};
  for (const key of ATTR_KEYS) if (typeof newest[key] === "string" && newest[key]) out[key] = String(newest[key]).slice(0, key === "fbc" || key === "fbp" ? 500 : 200);
  if (webUrl(newest.landing)) out.landing = webUrl(newest.landing).slice(0, 500);
  // t.js keeps the referring host only; the snippet keeps the whole address.
  const ref = typeof newest.referrer === "string" ? newest.referrer : "";
  if (webUrl(ref)) out.referrer = ref.slice(0, 500);
  else if (ref && newest === script) out.referrer = `https://${ref}/`;
  return metaIdentifiers(out, { cookie, at: Number(newest.at), now });
}

/** The browser's localStorage, or null where it is blocked. */
export function browserStorage(): VisitStore | null {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}

/** First and latest tracked visits, kept separately so a later ad cannot erase the first. */
export function rememberedJourney(options: Parameters<typeof rememberedVisit>[0] & { documentId?: number }): VisitJourney {
  if (options.gpc) return { tracking_consent: false };
  const now = options.now ?? Date.now();
  const read = (key: string): Record<string, unknown> => {
    try { return options.gpc ? {} : JSON.parse(options.storage?.getItem(key) || "{}") || {}; } catch { return {}; }
  };
  const touch = (raw: unknown, tracker = false): { value: Attribution; at: number } | null => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const source = raw as Record<string, unknown>;
    const at = Number(source.at);
    if (!Number.isFinite(at) || at > now || now - at >= VISIT_MS) return null;
    const value: Attribution = {};
    for (const key of ATTR_KEYS) if (typeof source[key] === "string" && source[key]) value[key] = source[key].slice(0, key === "fbc" || key === "fbp" ? 500 : 200);
    if (webUrl(source.landing)) value.landing = webUrl(source.landing).slice(0, 500);
    const ref = typeof source.referrer === "string" ? source.referrer : "";
    if (webUrl(ref)) value.referrer = ref.slice(0, 500);
    else if (tracker && /^[a-z0-9.-]+$/i.test(ref)) value.referrer = `https://${ref}/`;
    return ATTR_KEYS.some(key => value[key]) || value.referrer ? { value: metaIdentifiers(value, { at, now }), at } : null;
  };
  const history = read(JOURNEY_KEY);
  const tracker = read("nla_attr");
  // Read the previous visit before rememberedVisit saves the current click.
  const previous = touch(read(VISIT_KEY));
  const firsts = [touch(history.first), touch(tracker.first, true), previous].filter((v): v is NonNullable<typeof v> => !!v);
  const documentId = options.documentId && Number.isFinite(options.documentId) ? options.documentId : undefined;
  const lastSaved = [touch(history.last), touch(tracker.last, true), previous].filter((v): v is NonNullable<typeof v> => !!v).sort((a, b) => b.at - a.at)[0];
  // document.referrer does not change during SPA navigation or query cleanup.
  const sameDocument = documentId && (history.document_id === documentId || (lastSaved && lastSaved.at >= documentId));
  const hasNewClick = Object.keys(pickAttribution(options.search)).length > 0;
  const externalReferrer = webUrl(options.referrer) && hostOf(options.referrer) !== hostOf(options.here) ? webUrl(options.referrer) : '';
  const referral = !hasNewClick && !sameDocument && externalReferrer ? { landing: options.here.slice(0, 500), referrer: externalReferrer.slice(0, 500) } : null;
  const value: Attribution = referral ? metaIdentifiers(referral, { cookie: options.cookie, now }) : rememberedVisit(options);
  const tracked = ATTR_KEYS.some(key => value[key]) || value.referrer;
  if (!tracked) return value;
  const latest = { value, at: hasNewClick || referral ? now : lastSaved?.at ?? now };
  const first = firsts.sort((a, b) => a.at - b.at)[0] || latest;
  try {
    if (!options.gpc) {
      options.storage?.setItem(JOURNEY_KEY, JSON.stringify({ first: { ...first.value, at: first.at }, last: { ...latest.value, at: latest.at }, document_id: documentId }));
      if (referral) options.storage?.setItem(VISIT_KEY, JSON.stringify({ ...latest.value, at: latest.at }));
    }
  } catch { /* Blocked storage still permits this page's explicit attribution. */ }
  return { ...latest.value, first_touch: first.value, last_touch: latest.value };
}

/** Bounded iframe transport for the first visit; only known fields survive. */
export function firstTouchQuery(raw: unknown): Attribution {
  let source: unknown = raw;
  try { if (typeof raw === "string" && raw.length <= 5000) source = JSON.parse(raw); } catch { return {}; }
  if (!source || typeof source !== "object" || Array.isArray(source)) return {};
  const input = source as Record<string, unknown>;
  const out: Attribution = {};
  for (const key of ATTR_KEYS) if (typeof input[key] === "string" && input[key]) {
    const v = key === "fbc" || key === "fbp" ? validMetaId(key, input[key]) : input[key].slice(0, 200);
    if (v) out[key] = v;
  }
  for (const key of ["landing", "referrer"] as const) if (webUrl(input[key])) out[key] = webUrl(input[key]).slice(0, 500);
  return out;
}

/** The labelled rows a contact's Attribution block shows: only the ones with a value. */
export function attributionRows(a: Attribution | Record<string, string> | null | undefined): AttrRow[] {
  const src = (a || {}) as Record<string, string>;
  const rows: AttrRow[] = [];
  for (const key of [...ATTR_KEYS, "landing", "referrer"]) {
    const value = typeof src[key] === "string" ? src[key].trim() : "";
    if (!value) continue;
    const url = key === "landing" || key === "referrer";
    rows.push({ key, label: LABELS[key], value, kind: ID_KEYS.has(key) ? "id" : url && webUrl(value) ? "url" : "text" });
  }
  return rows;
}

/** The Leads table's short "Came from": "google / cpc", "Google Ads click", "facebook.com", or "". */
export function cameFrom(a: Attribution | Record<string, string> | null | undefined): string {
  const x = (a || {}) as Record<string, string | undefined>;
  if (x.utm_source) return x.utm_medium ? `${x.utm_source} / ${x.utm_medium}` : x.utm_source;
  if (x.gclid || x.gbraid || x.wbraid || x.gad_source || x.gad_campaignid) return "Google Ads click";
  if (x.fbclid || x.fbc) return "Meta click";
  if (x.msclkid) return "Microsoft Ads click";
  if (x.ttclid) return "TikTok click";
  if (x.li_fat_id) return "LinkedIn click";
  if (x.ref) return `Ref: ${x.ref}`;
  return hostOf(x.referrer);
}

const attr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Only an intentional marketing CTA, or a known NOLA form/calendar, receives tags. */
export function forwardMarketingUrl(href: string, visit: VisitJourney, { base, enabled = false }: { base: string; enabled?: boolean }): string {
  if (visit.tracking_consent === false || !href || href.startsWith('#')) return href;
  try {
    const source = new URL(base), destination = new URL(href, source);
    if (!/^https?:$/.test(destination.protocol) || destination.username || destination.password) return href;
    const host = destination.hostname.toLowerCase().replace(/^www\./, '');
    if (/(^|\.)(facebook\.com|fb\.com|instagram\.com|linkedin\.com|tiktok\.com|twitter\.com|x\.com|youtube\.com|youtu\.be|pinterest\.com|threads\.net|threads\.com|whatsapp\.com|wa\.me|messenger\.com|t\.me)$/.test(host)) return href;
    if (/(^|\/)(privacy|terms|legal|polic(?:y|ies)|cookies?|unsubscribe|share|sharer)(?:[/.?_-]|$)/i.test(destination.pathname) || destination.searchParams.get('action') === 'share') return href;
    const nolaHost = destination.origin === source.origin || ['nl-aos-ten.vercel.app', 'nola.nolimits.marketing'].includes(host);
    const native = nolaHost && /^\/(?:f|book)\/[^/]+\/?$/.test(destination.pathname);
    if (!enabled && !native) return href;
    let changed = false;
    const googleOwned = ['gclid', 'gbraid', 'wbraid'].some(key => destination.searchParams.has(key));
    const metaOwned = ['fbclid', 'fbc'].some(key => destination.searchParams.has(key));
    for (const key of ATTR_KEYS) {
      const value = visit[key];
      if (!value || destination.searchParams.has(key)) continue;
      if (googleOwned && ['gclid', 'gbraid', 'wbraid'].includes(key) || metaOwned && ['fbclid', 'fbc'].includes(key)) continue;
      const clean = key === 'fbc' || key === 'fbp' ? validMetaId(key, value) : value.slice(0, 200);
      if (clean) { destination.searchParams.set(key, clean); changed = true; }
    }
    return changed ? destination.toString() : href;
  } catch { return href; }
}

/**
 * The website embed: the form in an iframe that fits its height, plus a small script
 * that passes the visit's attribution into it. On the host page it reads the UTM
 * tags and click ids from the URL; when there are any it saves them, with the page
 * and the referring site, in that site's localStorage (nla_form_attr) for 90 days.
 * A visit without them uses the newest saved visit, its own or the one the NOLA
 * tracking script (t.js) keeps in nla_attr, so a click from an ad on another page
 * still reaches the form. Plain ES5, every step wrapped: it must never break the
 * host site, and the form loads even if all of it fails. Nothing is saved when the
 * browser sends Global Privacy Control. Snippets pasted before this keep working:
 * the form page reads the same keys from its URL either way.
 */
export function embedCode(link: string, slug: string, name: string, options: { page?: boolean } = {}): string {
  const id = `nla-${options.page ? 'page' : 'form'}-${slug.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  return `<iframe id="${id}" src="${attr(link)}" title="${attr(name)}" style="width:100%;min-height:${options.page ? 900 : 560}px;border:0" loading="lazy"></iframe>
<script>(function(){var f=document.getElementById("${id}");if(!f)return;
try{window.addEventListener("message",function(e){if(e.source===f.contentWindow&&e.data&&e.data.type==="nla-height")f.style.height=e.data.height+"px";});}catch(e){}
try{if(navigator.globalPrivacyControl===true){var fs=f.src.split("#");f.src=fs[0]+(fs[0].indexOf("?")<0?"?":"&")+"nla_consent=0"+(fs.length>1?"#"+fs.slice(1).join("#"):"");return;}var K="${ATTR_KEYS.join(" ")}".split(" "),L=location,here=L.protocol+"//"+L.host+L.pathname,now=new Date().getTime(),D=7776e6,a={},n=0,i,v,s,t,r=document.referrer||"",h=/^https?:\\/\\/([^\\/?#:]+)/.exec(r);
var valid=function(k,v){var m=typeof v==="string"&&v.length<=500&&/^fb\\.[0-9]\\.(\\d{13})\\.([A-Za-z0-9_-]+)$/.exec(v);return m&&+m[1]>=9466848e5&&+m[1]<=now+3e5&&(k!=="fbp"||/^\\d+$/.test(m[2]))?v:"";};
var ck=function(k){try{var m=new RegExp("(?:^|;\\\\s*)_"+k+"=([^;]*)").exec(document.cookie||"");return valid(k,m?decodeURIComponent(m[1]):"");}catch(e){return "";}};
var p=function(k){var m=new RegExp("[?&]"+k+"=([^&#]*)").exec(L.search);try{return m?decodeURIComponent(m[1].replace(/\\+/g," ")):"";}catch(e){return "";}};
var g=function(k){try{return navigator.globalPrivacyControl===true?null:JSON.parse(localStorage.getItem(k));}catch(e){return null;}};
var j=g("nla_form_touches")||{},old=g("nla_form_attr"),tr=g("nla_attr")||{},first=null,candidates=[j.first,tr.first,old],doc=typeof performance!=="undefined"?performance.timeOrigin:0,same=doc&&(j.document_id===doc||(old&&old.at>=doc)||(tr.last&&tr.last.at>=doc));
for(i=0;i<candidates.length;i++){v=candidates[i];if(v&&typeof v.at==="number"&&v.at<=now&&now-v.at<D&&(!first||v.at<first.at))first=v;}
if(!h||h[1].replace(/^www\\./,"")===L.hostname.replace(/^www\\./,""))r="";
for(i=0;i<K.length;i++){v=p(K[i]);if(v){v=K[i]==="fbc"||K[i]==="fbp"?valid(K[i],v):v.slice(0,200);if(v){a[K[i]]=v;n++;}}}
if(n){a.landing=here;if(r)a.referrer=r.slice(0,500);a.at=now;try{if(navigator.globalPrivacyControl!==true)localStorage.setItem("nla_form_attr",JSON.stringify(a));}catch(e){}}
else if(r&&!same){a={referrer:r.slice(0,500),landing:here,at:now};try{if(navigator.globalPrivacyControl!==true)localStorage.setItem("nla_form_attr",JSON.stringify(a));}catch(e){}}
else{s=g("nla_form_attr");t=g("nla_attr");t=t&&(t.last||t.first);if(!(s&&s.at<=now&&now-s.at<D))s=null;if(!(t&&t.at<=now&&now-t.at<D))t=null;
if(t&&(!s||t.at>s.at)){for(i=0;i<K.length;i++)if(t[K[i]])a[K[i]]=String(t[K[i]]);if(t.landing)a.landing=t.landing;if(t.referrer)a.referrer="https://"+t.referrer+"/";a.at=t.at;}
else if(s)a=s;else if(r)a.referrer=r.slice(0,500);}
var fc=[valid("fbc",a.fbc),ck("fbc"),valid("fbc",old&&old.fbc)],best="";for(i=0;i<fc.length;i++)if(fc[i]&&(!a.fbclid||fc[i].split(".").slice(3).join(".")===a.fbclid)){best=fc[i];break;}if(!best&&a.fbclid&&/^[A-Za-z0-9_-]{1,200}$/.test(a.fbclid))best=valid("fbc","fb.1."+(a.at||now)+"."+a.fbclid);delete a.fbc;if(best)a.fbc=best;v=valid("fbp",a.fbp)||ck("fbp");delete a.fbp;if(v)a.fbp=v;
var tracked=!!a.referrer;for(i=0;i<K.length;i++)if(a[K[i]])tracked=true;
if(tracked){if(!a.at)a.at=now;if(!first)first=a;try{localStorage.setItem("nla_form_attr",JSON.stringify(a));localStorage.setItem("nla_form_touches",JSON.stringify({first:first,last:a,document_id:doc}));}catch(e){}}
var parts=f.src.split("#"),base=parts[0],q="",add=function(k,v){if(!new RegExp("[?&]"+k+"=").test(base))q+=(base.indexOf("?")<0&&!q?"?":"&")+k+"="+encodeURIComponent(v);};add("page",here);add("nla_embed","1");K.push("landing","referrer");
for(i=0;i<K.length;i++)if(a[K[i]])add(K[i],a[K[i]]);
if(first&&tracked){var ft={};for(i=0;i<K.length;i++)if(typeof first[K[i]]==="string")ft[K[i]]=first[K[i]].slice(0,K[i]==="landing"||K[i]==="referrer"||K[i]==="fbc"||K[i]==="fbp"?500:200);if(ft.referrer&&!/^https?:/.test(ft.referrer))ft.referrer="https://"+ft.referrer+"/";add("first_touch",JSON.stringify(ft));}
f.src=base+q+(parts.length>1?"#"+parts.slice(1).join("#"):"");}catch(e){}})();</script>`;
}
